import { types, flow } from 'mobx-state-tree';
import { ApiStore } from './ApiStore';

// 'delivered' is the analyzer's success status (written as a literal string).
// 'failed' is the unified failure status (BOT_STATUS.FAILED).
const TERMINAL_STATUSES = ['delivered', 'failed'];
// Faster cadence while at least one meeting is mid-pipeline so users see the
// state transition shortly after it happens; relax to a slower cadence once
// everything is terminal.
const POLL_INTERVAL_IN_FLIGHT_MS = 10_000;
const POLL_INTERVAL_IDLE_MS = 30_000;

const SCOPE_STORAGE_KEY = 'myMeetings.scope';

const readPersistedScope = () => {
    try {
        const v = typeof window !== 'undefined' && window.localStorage
            ? window.localStorage.getItem(SCOPE_STORAGE_KEY)
            : null;
        return (v === 'self' || v === 'team') ? v : 'self';
    } catch {
        return 'self';
    }
};

const persistScope = (scope) => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return;
        if (scope === 'self' || scope === 'team') {
            window.localStorage.setItem(SCOPE_STORAGE_KEY, scope);
        }
    } catch {
        // localStorage can throw in private-browsing mode — swallow.
    }
};

// `days` is either a positive number (window in days) or the literal string 'all'.
// Stored as a string ('all' | '7' | '30' | ...) to keep the union simple in MST.
//
// `dateFrom` / `dateTo` are YYYY-MM-DD strings. When either is set, they take
// precedence over `days`. Setting a preset clears them.
const _myMeetingsStore = types.model('MyMeetingsStore', {
    meetings: types.optional(types.array(types.frozen()), []),
    scope: types.optional(types.enumeration(['self', 'team']), readPersistedScope()),
    days: types.optional(types.string, '30'),
    dateFrom: types.optional(types.string, ''),
    dateTo: types.optional(types.string, ''),
    searchQuery: types.optional(types.string, ''),
    lastLoadedAt: types.maybeNull(types.string),
    error: types.maybeNull(types.string),
    currentCompanyId: types.maybeNull(types.string),
}).volatile(() => ({
    pollHandle: null,
    pollInterval: null,
    // Volatile flag toggled by foreground loadMeetings calls so the page can
    // dim the list during scope/date-range refetches. ApiStore.isLoading
    // can't be used directly — it flips on for every API call going through
    // this store (including background polls), and adding `isLoading` as a
    // second volatile field collides with the model property on the composed
    // ApiStore. Hence a separate, foreground-only flag.
    isForegroundFetch: false,
}));

export const MyMeetingsStore = types.compose(_myMeetingsStore, ApiStore)
    .actions(self => ({
        setScope(scope) {
            if (scope !== 'self' && scope !== 'team') return;
            if (self.scope === scope) return;
            self.scope = scope;
            persistScope(scope);
        },

        setDays(days) {
            // Picking a preset clears any custom date range
            self.dateFrom = '';
            self.dateTo = '';
            if (days === 'all') {
                self.days = 'all';
                return;
            }
            const parsed = parseInt(days, 10);
            if (!Number.isFinite(parsed) || parsed < 1) return;
            self.days = String(parsed);
        },

        setDateFrom(date) {
            // Setting a custom date clears the preset
            self.dateFrom = (date || '').trim();
        },

        setDateTo(date) {
            self.dateTo = (date || '').trim();
        },

        clearDateRange() {
            self.dateFrom = '';
            self.dateTo = '';
        },

        setSearchQuery(query) {
            self.searchQuery = (query || '').trim();
        },

        loadMeetings: flow(function* loadMeetings(companyId, options = {}) {
            if (!companyId) return null;
            self.currentCompanyId = companyId;
            // `isBackgroundPoll` is set by the polling loop so the UI can decide
            // whether to show a refetch spinner. Foreground fetches (initial
            // load, scope toggle, date-range change) flip isLoading; polls don't.
            const isBackgroundPoll = options.isBackgroundPoll === true;
            if (!isBackgroundPoll) self.isForegroundFetch = true;

            const scope = options.scope || self.scope;
            const days = options.days || self.days;
            const dateFrom = options.dateFrom != null ? options.dateFrom : self.dateFrom;
            const dateTo = options.dateTo != null ? options.dateTo : self.dateTo;

            // If either date bound is set, send from/to and ignore days. Otherwise use days.
            const useDateRange = dateFrom || dateTo;
            const qs = new URLSearchParams({ scope });
            if (useDateRange) {
                if (dateFrom) qs.set('from', dateFrom);
                if (dateTo) qs.set('to', dateTo);
            } else {
                qs.set('days', days);
            }

            const url = `/companies/${companyId}/murphy/my-meetings?${qs.toString()}`;

            try {
                const response = yield self.getApi(url);
                self.meetings.replace(response.meetings || []);
                self.scope = response.scope || scope;
                if (!useDateRange && response.days != null) {
                    self.days = String(response.days);
                }
                self.lastLoadedAt = new Date().toISOString();
                self.error = null;
                self._maybeStartPolling();
                return response;
            } catch (err) {
                console.error('MyMeetingsStore: failed to load meetings', err);
                self.error = err.message || 'Failed to load meetings';
                throw err;
            } finally {
                if (!isBackgroundPoll) self.isForegroundFetch = false;
            }
        }),

        fetchTranscriptUrl: flow(function* fetchTranscriptUrl(companyId, botId) {
            if (!companyId || !botId) throw new Error('companyId and botId required');
            const url = `/companies/${companyId}/murphy/meetings/${botId}/transcript`;
            return yield self.getApi(url);
        }),

        _maybeStartPolling() {
            const hasInFlight = self.meetings.some(
                m => !TERMINAL_STATUSES.includes(m.status)
            );
            const interval = hasInFlight ? POLL_INTERVAL_IN_FLIGHT_MS : POLL_INTERVAL_IDLE_MS;
            // Restart the interval if the cadence needs to change (e.g. a
            // meeting just transitioned to terminal — relax to 30s).
            if (self.pollHandle && self.pollInterval !== interval) {
                self._stopPolling();
            }
            if (!self.pollHandle) {
                self._startPolling(interval);
            }
        },

        _startPolling(interval) {
            if (self.pollHandle) return;
            self.pollInterval = interval;
            self.pollHandle = setInterval(() => {
                if (self.currentCompanyId) {
                    self.loadMeetings(self.currentCompanyId, { isBackgroundPoll: true })
                        .catch(() => {});
                }
            }, interval);
        },

        _stopPolling() {
            if (self.pollHandle) {
                clearInterval(self.pollHandle);
                self.pollHandle = null;
                self.pollInterval = null;
            }
        },

        beforeDestroy() {
            self._stopPolling();
        },

        clear() {
            self.meetings.clear();
            self.lastLoadedAt = null;
            self.error = null;
            self._stopPolling();
        }
    }))
    .views(self => ({
        get hasMeetings() {
            return self.meetings.length > 0;
        },
        get isPolling() {
            return self.pollHandle !== null;
        },
        get inFlightCount() {
            return self.meetings.filter(m => !TERMINAL_STATUSES.includes(m.status)).length;
        },
        get hasCustomDateRange() {
            return Boolean(self.dateFrom) || Boolean(self.dateTo);
        },
        // Client-side search filter applied to the already-loaded list.
        // Matches meeting title and any participant name/email substring (case-insensitive).
        get filteredMeetings() {
            const q = self.searchQuery.toLowerCase();
            if (!q) return self.meetings.slice();
            return self.meetings.filter(m => {
                if ((m.meetingTitle || '').toLowerCase().includes(q)) return true;
                if ((m.salesPersonName || '').toLowerCase().includes(q)) return true;
                const participants = m.participants || [];
                return participants.some(p =>
                    (p.name || '').toLowerCase().includes(q) ||
                    (p.email || '').toLowerCase().includes(q)
                );
            });
        }
    }));

export default MyMeetingsStore;
